import { useState, useEffect, useCallback } from "react";
import { Box, Typography, CircularProgress, List, ListItem, ListItemText, Avatar, useTheme, Paper, ToggleButtonGroup, ToggleButton, Tabs, Tab } from "@mui/material";
import { tokens } from "../../theme";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';
import axios from "axios";
import { API_BASE_URL } from '../../config/api';

const TopSpsList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [richlist, setRichlist] = useState([]);
  const [burners, setBurners] = useState([]);
  const [todayBurners, setTodayBurners] = useState([]);
  const [yesterdayBurners, setYesterdayBurners] = useState([]);
  const [viewMode, setViewMode] = useState('burners');
  const [dailyTab, setDailyTab] = useState('today');
  
  // Fetch burners data only once when component mounts
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchBurners(),
          fetchDailyTopBurners()
        ]);
      } catch (err) {
        setError(`Error loading initial data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
    // Refreshe die Daten alle 60 Sekunden
    const intervalId = setInterval(() => {
      fetchDailyTopBurners();
    }, 60000);
    return () => clearInterval(intervalId);
  }, []); // Empty dependency array - runs only once on mount
  
  // Fetch mode-specific data when viewMode changes
  useEffect(() => {
    const fetchSpecificData = async () => {
      try {
        if (viewMode === 'holders' && richlist.length === 0) {
          setLoading(true);
          await fetchRichlist();
          setLoading(false);
        }
      } catch (err) {
        setError(`Error loading data: ${err.message}`);
        setLoading(false);
      }
    };
    
    fetchSpecificData();
  }, [viewMode, richlist.length]);

  const fetchRichlist = async () => {
    try {
      const response = await axios.get("https://api2.splinterlands.com/players/richlist?token_type=SPSTOTAL&player=");
      let list = [];
      if (Array.isArray(response.data)) {
        list = response.data;
      } else if (response.data && Array.isArray(response.data.richlist)) {
        list = response.data.richlist;
      }
      if (list.length > 0) {
        setRichlist(list.slice(0, 100)); // Top 100
      } else {
        setError("No richlist data received");
      }
    } catch (err) {
      setError("Error loading richlist");
    }
  };

  // Neue API: Top 200 Burner (all-time)
  const fetchBurners = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/sps-burns/burners/?limit=200`);
      if (Array.isArray(response.data.results)) {
        setBurners(response.data.results);
      } else {
        setError("No burner data received");
      }
    } catch (err) {
      setError("Error loading burner data");
    }
  };

  // Neue API: Top Burner heute/gestern
  const fetchDailyTopBurners = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/sps-burns/daily-top-burners/`);
      if (response.data && response.data.today && Array.isArray(response.data.today.data)) {
        setTodayBurners(response.data.today.data.slice(0, 200));
      }
      if (response.data && response.data.yesterday && Array.isArray(response.data.yesterday.data)) {
        setYesterdayBurners(response.data.yesterday.data.slice(0, 200));
      }
    } catch (err) {
      setError("Error loading daily burn data");
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };
  
  const handleDailyTabChange = (event, newTab) => {
    if (newTab !== null) {
      setDailyTab(newTab);
    }
  };
  
  const getTitle = () => {
    switch (viewMode) {
      case 'burners':
        return 'Top SPS Burners';
      case 'dailyBurners':
        return dailyTab === 'today' ? 'Today\'s Top Burners' : 'Yesterday\'s Top Burners';
      default:
        return 'Top SPS Holders';
    }
  };
  
  const getIcon = () => {
    switch (viewMode) {
      case 'burners':
        return <LocalFireDepartmentIcon sx={{ color: colors.redAccent[400], mr: 1 }} />;
      case 'dailyBurners':
        return dailyTab === 'today' ? 
          <CalendarTodayIcon sx={{ color: colors.redAccent[400], mr: 1 }} /> :
          <HistoryIcon sx={{ color: colors.redAccent[400], mr: 1 }} />;
      default:
        return <EmojiEventsIcon sx={{ color: colors.greenAccent[400], mr: 1 }} />;
    }
  };
  
  // Memoize display data to prevent unnecessary re-renders
  const displayData = useCallback(() => {
    let data = [];
    switch (viewMode) {
      case 'burners':
        data = burners;
        if (data.length === 0) {
          return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={2}>
              <InfoIcon sx={{ fontSize: '2rem', color: colors.grey[500], mb: 1 }} />
              <Typography variant="body2" color={colors.grey[300]} align="center">
                No all-time burner data available
              </Typography>
            </Box>
          );
        }
        return data.map((entry, idx) => (
          <ListItem key={`burner-${idx}`} sx={{ py: 0.5 }}>
            <Avatar sx={{ width: 28, height: 28, bgcolor: colors.redAccent[600], mr: 1 }}>
              {idx + 1}
            </Avatar>
            <ListItemText
              primary={<Typography variant="body2" color={colors.grey[100]} fontWeight={idx < 3 ? 'bold' : 'normal'}>{entry.account}</Typography>}
              secondary={<Typography variant="body1" color={colors.redAccent[400]} fontWeight="medium">{formatNumber(entry.total_burned)} SPS</Typography>}
            />
          </ListItem>
        ));
      case 'dailyBurners':
        // Je nach Tab entweder heutige oder gestrige Daten anzeigen
        data = dailyTab === 'today' ? todayBurners : yesterdayBurners;
        
        if (data.length === 0) {
          return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={2}>
              <InfoIcon sx={{ fontSize: '2rem', color: colors.grey[500], mb: 1 }} />
              <Typography variant="body2" color={colors.grey[300]} align="center">
                {dailyTab === 'today' 
                  ? "No burn transactions recorded today yet. Check back later."
                  : "No burn transactions were recorded yesterday."}
              </Typography>
            </Box>
          );
        }
        
        return data.map((entry, idx) => (
          <ListItem key={`${dailyTab}-${idx}`} sx={{ py: 0.5 }}>
            <Avatar sx={{ width: 28, height: 28, bgcolor: colors.redAccent[600], mr: 1 }}>
              {idx + 1}
            </Avatar>
            <ListItemText
              primary={<Typography variant="body2" color={colors.grey[100]} fontWeight={idx < 3 ? 'bold' : 'normal'}>{entry.account}</Typography>}
              secondary={<Typography variant="body1" color={colors.redAccent[400]} fontWeight="medium">{formatNumber(entry.total_burned)} SPS</Typography>}
            />
          </ListItem>
        ));
      default:
        data = richlist;
        if (data.length === 0) {
          return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={2}>
              <InfoIcon sx={{ fontSize: '2rem', color: colors.grey[500], mb: 1 }} />
              <Typography variant="body2" color={colors.grey[300]} align="center">
                No holder data available
              </Typography>
            </Box>
          );
        }
        return data.map((entry, idx) => (
          <ListItem key={`holder-${idx}`} sx={{ py: 0.5 }}>
            <Avatar sx={{ width: 28, height: 28, bgcolor: colors.greenAccent[600], mr: 1 }}>
              {idx + 1}
            </Avatar>
            <ListItemText
              primary={<Typography variant="body2" color={colors.grey[100]} fontWeight={idx < 3 ? 'bold' : 'normal'}>{entry.player}</Typography>}
              secondary={<Typography variant="body1" color={colors.greenAccent[400]} fontWeight="medium">{formatNumber(entry.balance)} SPS</Typography>}
            />
          </ListItem>
        ));
    }
  }, [viewMode, dailyTab, burners, todayBurners, yesterdayBurners, richlist, colors.redAccent, colors.greenAccent, colors.grey]);

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress /></Box>;
  }
  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Paper sx={{ bgcolor: colors.primary[400], p: 2, borderRadius: 2, height: '100%' }}>
      <Box display="flex" flexDirection="column" height="100%">
        <Box display="flex" alignItems="center" mb={1} justifyContent="space-between">
          <Box display="flex" alignItems="center">
            {getIcon()}
            <Typography variant="h6" fontWeight="bold" color={colors.grey[100]}>{getTitle()}</Typography>
          </Box>
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            aria-label="Display Mode"
          >
            <ToggleButton value="burners">
              <LocalFireDepartmentIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="dailyBurners">
              <CalendarTodayIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="holders">
              <EmojiEventsIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        {/* Tabs für Today/Yesterday, nur anzeigen wenn dailyBurners aktiv */}
        {viewMode === 'dailyBurners' && (
          <Box mb={1}>
            <Tabs 
              value={dailyTab}
              onChange={handleDailyTabChange}
              variant="fullWidth"
              sx={{
                minHeight: '36px',
                '& .MuiTab-root': {
                  minHeight: '36px',
                  typography: 'body2',
                  fontWeight: 'medium',
                },
                '& .Mui-selected': {
                  color: `${colors.redAccent[400]} !important`,
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: colors.redAccent[400],
                },
              }}
            >
              <Tab value="today" label="Today" />
              <Tab value="yesterday" label="Yesterday" />
            </Tabs>
          </Box>
        )}
        
        <Box sx={{ maxHeight: viewMode === 'dailyBurners' ? 260 : 300, overflowY: 'auto', flexGrow: 1 }}>
          <List dense>
            {displayData()}
          </List>
        </Box>
      </Box>
    </Paper>
  );
};

export default TopSpsList;
